import { Client } from "@/Common/Api/Client";
import { Recall } from "@/Common/Api/Communications/Dto/Recall";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { BaseGridExternalMessage } from "@/Common/Models/ExternalCommunications/GridExternalMessage";
import { FileType } from "@/Common/Models/FileType";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { CommunicationsCounters } from "@/Common/Models/MyleMenu/Tooltip/CommunicationsCounters";
import { IndividualMessageNote } from "@/Common/Models/NotesPopup/IndividualMessageNote";
import { CommunicationStateType, CommunicationsUIState, CommunicationsUIStateInbox, MessageOrTaskContext, MessageOrTaskEditContext } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LoadMessageTaskViewResponse } from "@/Communications/Api/Dto/Responses/LoadMessageTaskViewResponse";
import { MessageTaskEditContainer } from "@/Communications/Components/MessageTaskEdit/MessageTaskEditContainer";
import { CommunicationsStateModel } from "@/Communications/Models/CommunicationsStateModel";
import { MessageInfo } from "@/Communications/Models/MessageInfo";
import { MessageOrTaskItem } from "@/Communications/Models/MessageOrTaskItem";
import { MessageOrTasksTableItem } from "@/Communications/Models/MessageOrTaskTableItems";
import { RecallTableItem } from "@/Communications/Models/Recalls/RecallTableItems";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { NotesPopup_OptimizedV2 } from "../../Common/Components/NotesPopup/NotesPopup_OptimizedV2";
import { ExternalMessageInboxComponent } from "./ExternalCommunications/ExternalMessageInboxComponent";
import { ExternalMessageOutboxComponent } from "./ExternalCommunications/ExternalMessageOutboxComponent";
import { ExternalMessageFormComponent, ExternalMessageFormContext } from "./ExternalCommunications/Form/ExternalMessageFormComponent";
import { ExternalMessageViewComponent } from "./ExternalCommunications/View/ExternalMessageViewComponent";
import { MessageTaskViewContainer } from "./MessageTaskView/MessageTaskViewContainer";
import { PortalCommunicationCreateMessage } from "./PortalCommunication/PortalCommunicationCreateMessage";
import { PortalCommunicationInbox } from "./PortalCommunication/PortalCommunicationInbox";
import { PortalCommunicationViewWrapper } from "./PortalCommunication/PortalCommunicationViewWrapper";
import { RecallEdit_V2, RecallEdit_V2State } from "./Recalls/RecallEdit_V2";
import { RecallView_V2 } from "./Recalls/RecallView_V2";
import { MessageSearch_OptimizedV2 } from "./Search/MessageSearch_OptimizedV2";
import { RecallSearch_OptimizedV2 } from "./Search/RecallSearch_OptimizedV2";
import { TaskSearch_OptimizedV2 } from "./Search/TaskSearch_OptimizedV2";
import { CommunicationsTreeControl_OptimizedV2 } from "./CommunicationsTreeControl_OptimizedV2";
import { RecallNotesPopup } from "./RecallNotesPopup";

import styles from "./CommunicationsTab_OptimizedV2.module.scss";

interface ICommunicationsTab_OptimizedV2Props {

}

class CommunicationsTab_OptimizedV2State {
    public leftMenuOpen: boolean;
    public uiState: CommunicationsUIState;
    public inboxState: CommunicationsUIStateInbox;

    public selectedMessageId: string;
    public selectedExternalMessage: BaseGridExternalMessage;
    public externalMessageFormContext: ExternalMessageFormContext;

    public selectedMessage: MessageOrTaskItem;
    public messageTaskResponse: LoadMessageTaskViewResponse;
    public messageOrTaskEditContext: MessageOrTaskEditContext;

    public recallsCount: number;
    public messagesCount: number;
    public tasksCount: number;
    public externalMessagesCount: number;
    public portalCommunicationsUnreadCount: number;
    public portalCommunicationsFlaggedCount: number;

    public selectedRecall: Recall;
    public recallForPopup: RecallTableItem;

    public savedState: CommunicationsStateModel;

    public shouldReloadTaskSearch: boolean;
    public shouldReloadMessageSearch: boolean;
    public shouldReloadRecallSearch: boolean;
    public externalCommunicationInboxGridSessionId: string;
    public externalCommunicationOutboxGridSessionId: string;

    public shouldShowExternalCommunicationSharedViewers: boolean;
    public shouldDisableImport: boolean;

    // Note popup data
    public targetElement: EventTarget;
    public currentMessageNotes: IndividualMessageNote[];
    public messageId: string;
    public messageStatusId: string;
    public notesPopupOpen: boolean;
    public selectedStatusId: string;
    public notesPopupTop: number;
    public notesPopupLeft: number;
    public isPopupForMessages: boolean;
    public isRecallNotePopupOpen: boolean;

    // Patient sidebar
    public isSidebarOpen: boolean;
    public isAdvancedSidebarSearchView: boolean;

    public static getDefault(): CommunicationsTab_OptimizedV2State {
        const state = new CommunicationsTab_OptimizedV2State();

        state.leftMenuOpen = true;
        state.uiState = CommunicationsUIState.TasksSearch;
        state.inboxState = CommunicationsUIStateInbox.Inbox;
        state.selectedMessageId = "";

        state.recallsCount = 0;
        state.messagesCount = 0;
        state.tasksCount = 0;
        state.externalMessagesCount = 0;
        state.portalCommunicationsUnreadCount = 0;

        state.targetElement = { checked: false } as EventTarget;
        state.currentMessageNotes = [];
        state.messageId = "";
        state.messageStatusId = "";
        state.notesPopupOpen = false;
        state.selectedStatusId = "";
        state.notesPopupTop = 0;
        state.notesPopupLeft = 0;
        state.isPopupForMessages = false;

        state.shouldReloadTaskSearch = false;
        state.shouldReloadMessageSearch = false;
        state.shouldReloadRecallSearch = false;
        state.externalCommunicationInboxGridSessionId = "";
        state.externalCommunicationOutboxGridSessionId = "";

        state.selectedExternalMessage = null;
        state.externalMessageFormContext = null;

        state.shouldShowExternalCommunicationSharedViewers = false;

        state.isSidebarOpen = false;
        state.isAdvancedSidebarSearchView = false;

        state.savedState = new CommunicationsStateModel();
        return state;
    }
}

class CommunicationsTab_OptimizedV2 extends BaseReactComponent<ICommunicationsTab_OptimizedV2Props, CommunicationsTab_OptimizedV2State> {
    private _commStoreChangeListener: string = "";
    private sidebarStoreChangeListener: string = "";
    private timeoutreferenceCountCommunication = null;

    public static footerCSS: string = "ws-footer";
    public constructor(props: ICommunicationsTab_OptimizedV2Props) {
        super(props);

        const savedState = communicationsStore.communicationsTabSavedState?.communicationsTabSavedState
            ?? CommunicationsTab_OptimizedV2State.getDefault();

        this.state = savedState;
    }

    public componentDidMount(): void {
        super.addHandle(LocalOperations.Misc.MyleApp_Container_Click, () => this.closeNotePopup());

        this._commStoreChangeListener = communicationsStore.AddChangeListener(this.changeListener, this, "communicaiontabv2-communication-change");
        this.sidebarStoreChangeListener = sidebarStore.AddChangeListener(this.sidebarChangeListener, this, "communicaiontabv2-sidebar-change");
        this.countCommunications();
    }

    private changeListener(): void {
        let state = new CommunicationsTab_OptimizedV2State();
        state.savedState = communicationsStore.communicationsTabSavedState;
        this.setState(state);
    }

    private sidebarChangeListener(): void {
        const isOpen = sidebarStore.Model.get("IsOpen");
        const isAdvancedSidebarSearchView = sidebarStore.Model.get("IsAdvancedSearchView") && isOpen;
        this.mutateState(s => {
            s.isSidebarOpen = isOpen;
            s.isAdvancedSidebarSearchView = isAdvancedSidebarSearchView;
        });
    }

    public render(): JSX.Element {
        const leftMenuStateCss = this.state.leftMenuOpen ? "left-nav-menu documents communications transition" : "left-nav-menu communications transition closed";
        const leftMenuControlsCss = this.state.leftMenuOpen ? "left-nav-menu-handle responsive" : "left-nav-menu-handle";
        const portalCommunicationsView = this.state.uiState === CommunicationsUIState.PortalCommunicationView || this.state.uiState === CommunicationsUIState.PortalCommunicationCreate;
        let sidebarCss = "";
        if (this.state.isAdvancedSidebarSearchView) {
            sidebarCss = styles.advancedSidebarSearchViewOpen;
        }
        else if (this.state.isSidebarOpen) {
            sidebarCss = styles.sidebarSearchOpen;
        }

        const subContainerCss = portalCommunicationsView ? `${styles.portalCommunicationSubContainer} ${sidebarCss}` : "sub-container";
        const menuButtonCss = `${portalCommunicationsView ? styles.portalCommunicationMenuButton : ""} ${leftMenuControlsCss} ${styles.leftMenuControlHeight} `;

        return (
            <div className={subContainerCss}>
                <div className={`${leftMenuStateCss} ${styles.leftMenuControlHeight} ${styles.leftMenuControlHeight}`}>
                    <div className="hide-overflow">
                        <CommunicationsTreeControl_OptimizedV2
                            model={this.state}
                            messageCreateClick={() => this.messageOrTaskCreateClick(CommunicationsUIState.MessagesCreate)}
                            messageSearchClick={inboxType => this.messageSearchClick(inboxType)}
                            taskCreateClick={() => this.messageOrTaskCreateClick(CommunicationsUIState.TasksCreate)}
                            taskSearchClick={inboxType => this.taskSearchClick(inboxType)}
                            recallCreateClick={() => this.recallCreateClick()}
                            recallSearchClick={() => this.handleRecallSearchClick()}
                            externalCommunicationCreateClick={() => this.handleExternalCommunicationCreateClick()}
                            externalCommunicationSearchClick={inboxType => this.handleExternalCommunicationSearchClick(inboxType)}
                            portalCommunicationSearchClick={inboxType => this.handlePortalCommunicationClick(CommunicationsUIState.PortalCommunicationInbox, inboxType)}
                            portalCommunicationCreateClick={() => this.handlePortalCommunicationClick(CommunicationsUIState.PortalCommunicationCreate, this.state.inboxState)}
                            recallsCount={this.state.recallsCount}
                            messagesCount={this.state.messagesCount}
                            tasksCount={this.state.tasksCount}
                            externalMessagesCount={this.state.externalMessagesCount}
                            portalCommunicationsUnreadCount={this.state.portalCommunicationsUnreadCount}
                            portalCommunicationsFlaggedCount={this.state.portalCommunicationsFlaggedCount}
                            selectedInbox={this.state.inboxState}
                            selectedUiState={this.state.uiState}
                        />
                    </div>
                </div>
                <div className={menuButtonCss}>
                    <div onClick={() => this.mutateState(s => s.leftMenuOpen = !s.leftMenuOpen)} className="slide-menu-icon">
                        <span />
                        <span />
                        <span />
                    </div>
                </div>
                {this.renderMainView()}
                {this.renderNotePopup()}
                {this.renderRecallNotePopup()}
            </div>
        );
    }

    private handleRecallSearchClick(): void {
        this.mutateState(s => {
            s.uiState = CommunicationsUIState.RecallsSearch;
            s.savedState.recallViewSavedState = null;
            s.shouldReloadRecallSearch = true;
        });
    }

    private messageSearchClick(inboxType: CommunicationsUIStateInbox): void {
        this.mutateState(s => {
            s.uiState = CommunicationsUIState.MessagesSearch;
            s.inboxState = inboxType;
            s.shouldReloadMessageSearch = true;
        });
    }

    private taskSearchClick(inboxType: CommunicationsUIStateInbox): void {
        this.mutateState(s => {
            s.uiState = CommunicationsUIState.TasksSearch;
            s.inboxState = inboxType;
            s.shouldReloadTaskSearch = true;
        });
    }

    private messageOrTaskCreateClick(uiState: CommunicationsUIState): void {
        this.mutateState(s => {
            s.uiState = uiState;
            s.selectedMessage = null;
            s.messageTaskResponse = null;
            s.savedState.messageTaskEditContainerSavedState = null;
            s.inboxState = null;
        });
    }

    private recallCreateClick(): void {
        this.mutateState(s => {
            s.savedState.recallEditSavedState = s.savedState.recallEditSavedState && cacheStore.ClinicSettings.EnableAutofillRecipientInCommunicationTab
                ? { ...RecallEdit_V2State.getDefault(), selectedClinicians: sessionStore.prefillRecallSelectedClinicians ?? [] }
                : null;
            s.uiState = CommunicationsUIState.RecallsCreate;
        });
    }

    private handleExternalCommunicationCreateClick(): void {
        this.mutateState(s => {
            s.uiState = CommunicationsUIState.ExternalCommunicationCreate;
            s.inboxState = CommunicationsUIStateInbox.Inbox;
            s.savedState.externalMessageFormState = null;
            s.selectedExternalMessage = null;
            s.externalMessageFormContext = null;
        });
    }

    private handleExternalCommunicationSearchClick(inboxType: CommunicationsUIStateInbox): void {
        this.mutateState(s => {
            s.inboxState = inboxType;
            switch (inboxType) {
                // Both Inbox state for xcom inbox share same component
                case CommunicationsUIStateInbox.Inbox:
                case CommunicationsUIStateInbox.Trash:
                    s.uiState = CommunicationsUIState.ExternalCommunicationInbox;
                    s.externalCommunicationInboxGridSessionId = sessionStore.newId().toString();
                    break;
                case CommunicationsUIStateInbox.Sent:
                    s.uiState = CommunicationsUIState.ExternalCommunicationOutbox;
                    s.externalCommunicationOutboxGridSessionId = sessionStore.newId().toString();
                    break;
            }
        });
    }

    private countCommunications(): void {
        if (this.timeoutreferenceCountCommunication !== -1)
            clearTimeout(this.timeoutreferenceCountCommunication);

        this.timeoutreferenceCountCommunication = window.setTimeout(() => {
            Client.Postv2(
                ServerOperations.Server.Myle_CountCommunication,
                {
                    DoctorContactId: sessionStore.individualContactId
                },
                response => {
                    if (response) {
                        this.mutateState(s => {
                            s.recallsCount = response.RecallsCount;
                            s.messagesCount = response.UnreadMessagesCount;
                            s.tasksCount = response.UnreadTasksCount;
                            s.externalMessagesCount = response.UnreadExternalMessagesCount;

                            if (cacheStore.ClinicSettings.EnableBidirectionalCommunication) {
                                s.portalCommunicationsUnreadCount = response.UnreadPortalCommunicationsCount;
                                s.portalCommunicationsFlaggedCount = response.FlaggedPortalCommunicationsCount;
                            }
                        });

                        const topCount = response.UnreadMessagesCount + response.UnreadTasksCount + (response.UnreadExternalMessagesCount || 0);
                        const bottomCount = response.PastDueTasksCount;

                        const communicationsCounters = new CommunicationsCounters(
                            response.UnreadMessagesCount,
                            response.UnreadTasksCount,
                            response.UnreadExternalMessagesCount,
                            response.PastDueTasksCount
                        );

                        cacheStore.UpdateTab(
                            MyleTabKey.Communications,
                            topCount,
                            bottomCount,
                            null,
                            null,
                            true,
                            { communicationsCounters: communicationsCounters }
                        );
                    }
                }
            );
        }, 2500);
    }

    private renderMainView(): JSX.Element {
        let viewToReturn: JSX.Element = null;
        switch (this.state.uiState) {
            case CommunicationsUIState.MessagesCreate:
                viewToReturn = this.renderMessageOrTaskEdit(false);
                break;
            case CommunicationsUIState.MessagesSearch:
                viewToReturn = this.renderMessageSearch();
                break;
            case CommunicationsUIState.MessagesView:
                viewToReturn = this.renderTaskOrMessageView(false);
                break;

            case CommunicationsUIState.TasksSearch:
                viewToReturn = this.renderTaskSearch();
                break;

            case CommunicationsUIState.TasksView:
                viewToReturn = this.renderTaskOrMessageView(true);
                break;

            case CommunicationsUIState.TasksCreate:
                viewToReturn = this.renderMessageOrTaskEdit(true);
                break;

            case CommunicationsUIState.RecallsSearch:
                viewToReturn = (
                    <RecallSearch_OptimizedV2
                        shouldReload={this.state.shouldReloadRecallSearch}
                        onLoadSuccess={() => this.mutateState(s => s.shouldReloadRecallSearch = false)}
                        selectedRecallRowClick={row => this.loadRecall(row.Id)}
                        showNotesPopup={(recall, e) => this.showRecallNotePopup(recall, e)}
                        savedState={this.state.savedState.recallSearchSavedState}
                        settingSavedState={this.state.savedState.recallSettingContainerSavedState}
                    />
                );
                break;

            case CommunicationsUIState.RecallsCreate:
                viewToReturn = (
                    <RecallEdit_V2
                        onResponseCallback={() => this.mutateState(s => {
                            s.uiState = CommunicationsUIState.RecallsSearch;
                            this.countCommunications();
                        })}
                        savedState={this.state.savedState.recallEditSavedState}
                    />
                );
                break;

            case CommunicationsUIState.RecallsView:
                viewToReturn = (
                    <RecallView_V2
                        model={this.state.selectedRecall}
                        onResponseCallback={() => this.mutateState(s => {
                            s.uiState = CommunicationsUIState.RecallsSearch;
                            this.countCommunications();
                        })}
                        savedState={this.state.savedState.recallViewSavedState}
                    />
                );
                break;

            case CommunicationsUIState.ExternalCommunicationCreate:
                viewToReturn = (
                    <ExternalMessageFormComponent
                        messageId={this.state.selectedExternalMessage && this.state.selectedExternalMessage.messageId}
                        context={this.state.externalMessageFormContext}
                        cacheState={this.state.savedState && this.state.savedState.externalMessageFormState}
                        onClose={() => this.mutateState(s => s.uiState = CommunicationsUIState.ExternalCommunicationInbox)}
                        onSend={() => this.mutateState(s => s.uiState = CommunicationsUIState.ExternalCommunicationInbox)}
                    />
                );
                break;

            case CommunicationsUIState.ExternalCommunicationOutbox:
                viewToReturn = (
                    <ExternalMessageOutboxComponent
                        onMessageRowClick={message => this.handleMessageClick(message, false, true)}
                        gridSessionId={this.state.externalCommunicationOutboxGridSessionId}
                    />
                );
                break;

            case CommunicationsUIState.ExternalCommunicationInbox:
                viewToReturn = (
                    <ExternalMessageInboxComponent
                        onMessageRowClick={message => this.handleMessageClick(message, true)}
                        gridSessionId={this.state.externalCommunicationInboxGridSessionId}
                        savedState={this.state.savedState.externalMessageInboxSearchSettingsState}
                        inboxType={this.state.inboxState}
                        communicationsCount={() => this.countCommunications()}
                    />
                );
                break;

            case CommunicationsUIState.ExternalCommunicationView:
                viewToReturn = (
                    <ExternalMessageViewComponent
                        messageId={this.state.selectedExternalMessage.messageId}
                        title={cacheStore.GetString("ExternalCommunicationsLabel")}
                        showPatientBanner
                        showFooterControls
                        showDeleteControl={this.state.inboxState === CommunicationsUIStateInbox.Inbox}
                        showSharedViewers={this.state.shouldShowExternalCommunicationSharedViewers}
                        disableImport={this.state.shouldDisableImport}
                        onLoaded={() => this.countCommunications()}
                        handleReplyOrForwardClick={(messageModel, context) => this.handleExternalCommunicationReplyOrForwardClick(messageModel, context)}
                        onDelete={() => this.handleExternalCommunicationSearchClick(this.state.inboxState)}
                    />
                );
                break;
            case CommunicationsUIState.PortalCommunicationInbox:
            {
                viewToReturn = (
                    <PortalCommunicationInbox
                        key={this.state.inboxState}
                        inboxType={this.state.inboxState}
                        onPortalCommunicationItemClick={sharedItemId => this.handlePortalCommunicationClick(CommunicationsUIState.PortalCommunicationView, this.state.inboxState, sharedItemId)}
                    />
                );

                break;
            }
            case CommunicationsUIState.PortalCommunicationView:
            {
                const { currentSharedItemId } = communicationsStore;

                if (!currentSharedItemId)
                    return;

                viewToReturn = (
                    <PortalCommunicationViewWrapper
                        currentSharedItemId={currentSharedItemId}
                        communicationsUIStateInbox={this.state.inboxState}
                        onCloseCommunication={() => this.handlePortalCommunicationClick(CommunicationsUIState.PortalCommunicationInbox, this.state.inboxState)}
                        onPortalCommunicationsCountRefresh={() => this.countCommunications()}
                    />
                );

                break;
            }
            case CommunicationsUIState.PortalCommunicationCreate:
            {
                viewToReturn = (
                    <PortalCommunicationCreateMessage
                        onClose={() => this.handlePortalCommunicationClick(CommunicationsUIState.PortalCommunicationInbox, this.state.inboxState)}
                        usedDynamicHeight
                    />
                );

                break;
            }
        }

        return viewToReturn;
    }

    private renderTaskOrMessageView(isTask: boolean): JSX.Element {
        return (
            <MessageTaskViewContainer
                individualMessageId={this.state.selectedMessageId}
                title={cacheStore.GetString("CommunicationsLabel")}
                showPatientBanner={true}
                showAddNoteControl={true}
                communicationUIState={this.state.inboxState}
                onMessageRead={() => this.countCommunications()}
                onRightButtonsClick={(model, context) => {
                    this.mutateState(s => {
                        s.savedState.messageTaskEditContainerSavedState = null;
                        s.messageTaskResponse = model;
                        s.selectedMessage = null;
                        s.messageOrTaskEditContext = context;
                        s.uiState = isTask ? CommunicationsUIState.TasksCreate : CommunicationsUIState.MessagesCreate;
                    });
                }}
                onLeftButtonsClick={() => this.mutateState(s => {
                    s.uiState = isTask ? CommunicationsUIState.TasksSearch : CommunicationsUIState.MessagesSearch;
                    this.countCommunications();
                })}
                savedState={this.state.savedState.messageTaskViewContainerSavedState}
                className="pad-left-15 pad-right-15"
            />
        );
    }

    private renderMessageOrTaskEdit(isTask: boolean): JSX.Element {
        const taskEditSelectedClinicians = sessionStore.prefillTaskSelectedClinicians ?? [];
        const messageEditSelectedClinicians = sessionStore.prefillMessageSelectedClinicians ?? [];

        return (
            <MessageTaskEditContainer
                key={isTask ? "taskEdit" : "messageEdit"}
                context={MessageOrTaskContext.Communications}
                isTask={isTask}
                onResponseCallback={() => this.mutateState(s => {
                    s.savedState.messageTaskEditContainerSavedState = null;
                    s.uiState = isTask ? CommunicationsUIState.TasksSearch : CommunicationsUIState.MessagesSearch;
                    this.countCommunications();
                })}
                messageTaskModel={this.state.selectedMessage}
                messageTaskResponse={this.state.messageTaskResponse}
                attachedObjects={(this.state.messageTaskResponse && this.state.messageTaskResponse.AttachedObjects)
                    ? this.state.messageTaskResponse.AttachedObjects
                        .filter(ao => ao.Type !== FileType.LAB)
                        .map(x => ExportableFileHelper.fromAttachedObject(x))
                    : []
                }
                editContext={this.state.messageOrTaskEditContext}
                savedState={this.state.savedState.messageTaskEditContainerSavedState}
                tempAttachedLabFillerID={this.state.messageTaskResponse ? this.state.messageTaskResponse.AttachedLab : ""}
                clickableTextCssClassName="communications-tab-new-message"
                selectedClinicians={isTask ? taskEditSelectedClinicians : messageEditSelectedClinicians}
            />
        );
    }

    private renderMessageSearch(): JSX.Element {
        return (
            <MessageSearch_OptimizedV2
                inboxType={this.state.inboxState}
                shouldReload={this.state.shouldReloadMessageSearch}

                onLoadSuccess={() => this.mutateState(s => s.shouldReloadMessageSearch = false)}

                selectedMessageRowClick={(message: MessageOrTaskItem) => {
                    // different loading logic here because
                    // drafts message are loaded directly in edit component mode - while for other inboxState we first go through the view component
                    if (message.MessageState == CommunicationsUIStateInbox.Drafts) {
                        this.loadDraftTaskOrMessage(message);
                    }
                    else {
                        this.mutateState(s => {
                            s.selectedMessageId = message.IM_ID;
                            s.uiState = CommunicationsUIState.MessagesView;
                            s.inboxState = message.MessageState;
                        });
                    }
                }}
                showNotesPopup={(rowData: MessageOrTasksTableItem, e: React.MouseEvent) => this.showMessageOrTaskNotePopup(rowData, e, true)}
                communicationsCount={() => this.countCommunications()}
                savedState={this.state.savedState.messageSearchSavedState}
                settingsSavedSate={this.state.savedState.messageSettingContainerSavedState}
            />
        );
    }

    private renderTaskSearch(): JSX.Element {
        return (
            <TaskSearch_OptimizedV2
                inboxType={this.state.inboxState}
                shouldReload={this.state.shouldReloadTaskSearch}
                onLoadSuccess={() => this.mutateState(s => s.shouldReloadTaskSearch = false)}
                showNotesPopup={(rowData: MessageOrTasksTableItem, e: React.MouseEvent) => this.showMessageOrTaskNotePopup(rowData, e, false)}
                selectedTaskRowClick={(task: MessageOrTaskItem) => {
                    if (task.MessageState === CommunicationsUIStateInbox.Drafts) {
                        // different loading logic here because
                        // drafts message are loaded directly in edit component mode - while for other inboxState we first go through the view component
                        this.loadDraftTaskOrMessage(task);
                    }
                    else {
                        this.mutateState(s => {
                            s.selectedMessageId = task.IM_ID;
                            s.uiState = CommunicationsUIState.TasksView;
                            s.inboxState = task.MessageState;
                        });
                    }
                }}
                communicationsCount={() => this.countCommunications()}
                savedState={this.state.savedState.taskSearchSavedState}
                settingSavedState={this.state.savedState.taskSettingContainerSavedState}
            />
        );
    }

    private renderNotePopup(): JSX.Element {
        if (!this.state.notesPopupOpen) return null;

        return (
            <NotesPopup_OptimizedV2
                controlId="task_notes_dropdown_main"
                dataCy="communicationNotesPopup"
                itemId={this.state.messageId}
                notes={this.state.currentMessageNotes}
                leftPosition={this.state.notesPopupLeft}
                topPosition={this.state.notesPopupTop}
                selectedStatus={this.state.selectedStatusId}
                selectStatusOptions={cacheStore.FindLookupsByTypeCode("TaskStatus").map(p => p.ToComboboxItem())}
                messageStatusId={this.state.messageStatusId}
                showStatusControls={!this.state.isPopupForMessages}
                onCancel={() => this.mutateState(s => {
                    s.notesPopupOpen = false;
                    $("table.myle-table tbody tr").removeClass("active");
                })}
                onSave={(note: string, messageId: string, status?: string, messageStatusId?: string) => {
                    this.saveMessageNote({
                        MessageOrTaskId: messageId,
                        Note: note,
                        IndividualMessageStateId: messageStatusId,
                        TaskStatusLookupId: status
                    });
                }}
                footerClassName={CommunicationsTab_OptimizedV2.footerCSS}
            />
        );
    }

    private renderRecallNotePopup(): JSX.Element {
        if (!this.state.isRecallNotePopupOpen) return null;

        return (
            <RecallNotesPopup
                controlId="recall_notes_dropdown_main"
                notes={this.state.recallForPopup.Model.RecallCommunications}
                recall={this.state.recallForPopup}
                leftPosition={this.state.notesPopupLeft}
                topPosition={this.state.notesPopupTop}
                onClose={() => {
                    this.mutateState(s => s.isRecallNotePopupOpen = false);
                    sessionStore.hideGlobalOverlay();
                }}
                loadRecallRef={() => this.mutateState(s => s.shouldReloadRecallSearch = true)}
                removeActiveClassFromTable={() => this.removeActiveClassFromTable()}
            />
        );
    }

    public componentWillUnmount(): void {
        communicationsStore.RemoveChangeListener(this._commStoreChangeListener);
        sidebarStore.RemoveChangeListener(this.sidebarStoreChangeListener);

        setTimeout(() => sessionStore.dispatch(
            LocalOperations.Communications.CommunicationStatesSave,
            {
                type: CommunicationStateType.Tab,
                state: this.state
            }
        ), 0);

        super.componentWillUnmount();
    }

    private saveMessageNote(requestData: { MessageOrTaskId: string, Note: string, IndividualMessageStateId: string, TaskStatusLookupId: string }): void {
        Client.Postv2(
            ServerOperations.Server.Myle_SaveMessageNote,
            requestData,
            response => {
                if (response) {
                    this.mutateState(s => {
                        s.notesPopupOpen = false;
                        s.shouldReloadTaskSearch = true;
                        s.shouldReloadMessageSearch = true;
                    });
                    $("table.myle-table tbody tr").removeClass("active");
                    sessionStore.hideGlobalOverlay();
                }
            }
        );
    }

    private loadRecall(recallId: string): void {
        Client.Postv2(
            ServerOperations.Server.Myle_GetRecall,
            {
                RecallId: recallId,
                IncludeCommunications: true
            },
            response => {
                if (response) {
                    const recall: Recall = response.Recall;
                    this.mutateState(s => {
                        s.selectedRecall = recall;
                        s.uiState = CommunicationsUIState.RecallsView;
                    });
                }
            }
        );
    }

    private showMessageOrTaskNotePopup(rowData: MessageOrTasksTableItem, e: React.MouseEvent, isForMessage: boolean): void {
        let eNativeTarget = e.nativeEvent.target;
        let eClientY = e.clientY;
        let eClientX = e.clientX - NotesPopup_OptimizedV2.popupWidth;

        this.commonShowNotePopup(
            e,
            "#task_notes_dropdown_main",
            null,
            () => Client.Postv2(
                ServerOperations.Server.Myle_LoadNotesForTasksMessages,
                { MessageOrTaskId: rowData.Model.IM_ID },
                response => {
                    if (response) {
                        this.mutateState(s => {
                            s.targetElement = eNativeTarget;
                            s.currentMessageNotes = response.Notes || rowData.Model.Notes || [];
                            s.messageId = rowData.Model.IM_ID;
                            s.messageStatusId = rowData.Model.IMS_ID;
                            s.notesPopupOpen = true;
                            s.selectedStatusId = rowData.Model.TaskStatusLookupId;
                            s.notesPopupTop = eClientY;
                            s.notesPopupLeft = eClientX;
                            s.isPopupForMessages = isForMessage;
                        });
                    }
                }
            )
        );
    }

    private showRecallNotePopup(rowData: RecallTableItem, e: React.MouseEvent): void {
        let eTarget = e.nativeEvent.target;
        let eClientY = e.clientY;
        let eClientX = e.clientX - 600;

        this.commonShowNotePopup(
            e,
            "#recall_notes_dropdown_main",
            () => this.mutateState(s => {
                s.targetElement = eTarget;
                s.isRecallNotePopupOpen = true;
                s.notesPopupTop = eClientY;
                s.notesPopupLeft = eClientX;
                s.recallForPopup = rowData;
            })
        );
    }

    private commonShowNotePopup(e: React.MouseEvent, popupId: string, mutation?: () => void, serverCall?: () => void): void {
        $(e.target).closest("tr").addClass("active");
        let a = $("." + CommunicationsTab_OptimizedV2.footerCSS).position().top;
        a = a - (e.clientY);

        mutation && mutation();

        const popup = $(popupId);

        popup.css("visibility", "hidden");

        window.setTimeout(() => {
            popup.removeClass("displaced");
            const h = popup.height();
            popup.find("textarea").focus();
            if (h > a) {
                const t = popup.position().top;
                popup.css("top", (t - h) + "px");
                popup.addClass("displaced");
            }
            popup.css("visibility", "visible");
        }, 30);

        if (serverCall) serverCall();
    }

    private closeNotePopup(): void {
        this.mutateState(s => {
            s.notesPopupOpen = false;
            s.isRecallNotePopupOpen = false;
        });
    }

    private removeActiveClassFromTable(): void {
        $("table.myle-table tbody tr").removeClass("active");
    }

    private loadDraftTaskOrMessage(message: MessageOrTaskItem): void {
        if (!message || !message.IM_ID) return;

        const requestData = {
            IndividualMessageId: message.IM_ID
        };

        Client.Postv2(
            ServerOperations.Server.Myle_Communications_LoadMessageTaskView,
            requestData,
            response => this.handleLoadDraftTaskOrMessage(response, message)
        );
    }

    private handleLoadDraftTaskOrMessage(response: LoadMessageTaskViewResponse, message: MessageOrTaskItem): void {
        this.mutateState(s => {
            s.savedState.messageTaskEditContainerSavedState = null;
            s.messageTaskResponse = response;
            s.selectedMessage = message;
            s.uiState = message.IsTask ? CommunicationsUIState.TasksCreate : CommunicationsUIState.MessagesCreate;
            s.inboxState = CommunicationsUIStateInbox.Drafts;
        });
    }

    private handleMessageClick(message: BaseGridExternalMessage, showSharedViewers: boolean, disableImport: boolean = false): void {
        this.mutateState(s => {
            s.savedState.externalMessageFormState = null;
            s.selectedExternalMessage = message;
            s.shouldShowExternalCommunicationSharedViewers = showSharedViewers;
            s.uiState = CommunicationsUIState.ExternalCommunicationView;
            s.shouldDisableImport = disableImport;
            if (message.isFailedSend) {
                s.uiState = CommunicationsUIState.ExternalCommunicationCreate;
                s.externalMessageFormContext = ExternalMessageFormContext.Resend;
            }
        });
    }

    private handleExternalCommunicationReplyOrForwardClick(messageModel: MessageInfo, context: ExternalMessageFormContext): void {
        this.mutateState(s => {
            s.uiState = CommunicationsUIState.ExternalCommunicationCreate;
            s.externalMessageFormContext = context;
            s.selectedExternalMessage.messageId = messageModel.message.Id;
        });
    }

    private handlePortalCommunicationClick(view: CommunicationsUIState, inboxType: CommunicationsUIStateInbox, currentSharedItemId: string = null): void {
        this.setCurrentSharedItemId(currentSharedItemId);

        this.mutateState(p => {
            p.uiState = view;
            p.inboxState = inboxType;
        });

        this.countCommunications();
    }

    private setCurrentSharedItemId(currentSharedItemId: string): void {
        sessionStore.dispatch(LocalOperations.Communications.SetCurrentSharedItemId, {
            currentSharedItemId: currentSharedItemId
        });
    }
}

export { CommunicationsTab_OptimizedV2, CommunicationsTab_OptimizedV2State };
